import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

declare const require: any;

declare global {
  interface Window {
    mapkit: any;
  }
}

type AppleMapType = "standard" | "satellite" | "hybrid";

type Props = {
  tokenUrl: string;
  type?: AppleMapType;
};

const MAPKIT_SCRIPT_ID = "apple-mapkit-js";

function loadMapKitScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.mapkit) {
      resolve();
      return;
    }

    const existingScript = document.getElementById(MAPKIT_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () =>
        reject(new Error("Failed to load Apple MapKit JS"))
      );
      return;
    }

    const script = document.createElement("script");
    script.id = MAPKIT_SCRIPT_ID;
    script.src = "https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js";
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Apple MapKit JS"));

    document.body.appendChild(script);
  });
}

async function getMapKitToken(tokenUrl: string): Promise<string> {
  const response = await fetch(tokenUrl, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Unable to get Apple MapKit token: ${errorText}`);
  }

  const data = await response.json();

  if (!data.token || typeof data.token !== "string") {
    throw new Error("Apple MapKit token missing or invalid");
  }

  return data.token;
}

function initMapKit(token: string) {
  if (!window.mapkit) {
    throw new Error("window.mapkit is not loaded");
  }

  window.mapkit.init({
    authorizationCallback: (done: (token: string) => void) => {
      done(token);
    },
    language: "fr",
  });
}

function loadMapKitMutantPlugin() {
  require("@independo/leaflet.mapkitmutant");
}

function getAppleMapType(type: AppleMapType) {
  if (!window.mapkit?.Map?.MapTypes) return undefined;

  switch (type) {
    case "satellite":
      return window.mapkit.Map.MapTypes.Satellite;

    case "hybrid":
      return window.mapkit.Map.MapTypes.Hybrid;

    case "standard":
    default:
      return window.mapkit.Map.MapTypes.Standard;
  }
}

export default function AppleMapkitLeafletLayer({
  tokenUrl,
  type = "standard",
}: Props) {
  const map = useMap();
  const layerRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;

    async function addAppleLayer() {
      try {
        await loadMapKitScript();

        if (cancelled) return;

        const token = await getMapKitToken(tokenUrl);

        if (cancelled) return;

        initMapKit(token);

        if (cancelled) return;

        loadMapKitMutantPlugin();

        if (cancelled) return;

        if (layerRef.current) {
          map.removeLayer(layerRef.current);
          layerRef.current = null;
        }

        const layer = (L as any).mapkitMutant({
          authorizationCallback: (done: (token: string) => void) => {
            done(token);
          },
          mapkitOptions: {
            mapType: getAppleMapType(type),
          },
        });

        layer.addTo(map);
        layerRef.current = layer;
      } catch (error) {
        console.error("Apple MapKit Leaflet layer error:", error);
      }
    }

    addAppleLayer();

    return () => {
      cancelled = true;

      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, tokenUrl, type]);

  return null;
}